import Select from 'react-select';
import { InternalExternalLink, FileLink } from './read-more-link-type';
import { getPosts, decodeUnicode } from '@helpers/post-block-utils';
import { READ_MORE_TYPE_LINK } from '@constants';
import moment from 'moment';

function registerPostOptionsPlugin({
  i18n: { __ },
  blockEditor: { useSettings },
  element: { useState, useEffect },
  data: { dispatch, withSelect },
  components: {
    SelectControl,
    TextControl,
    ToggleControl,
    DateTimePicker,
    Button,
  },
  editor: { PluginDocumentSettingPanel },
  blockEditor: { MediaUpload, MediaUploadCheck },
}) {
  const {
    B3RG_IN_EX_LINK,
    B3RG_FI_LINK,
    B3RG_PO_LINK,
    B3RG_NONE,
    B3RG_DE_LINK,
  } = READ_MORE_TYPE_LINK;

  const PostOptionsSettings = ({
    postMeta,
    currentPostId,
    featuredImageUrl,
  }) => {
    //NOSONAR
    const {
      learn_more_label: learnMoreLabelMetaValue,
      learn_more_type: learnMoreTypeMetaValue,
      learn_more_link_file: learnMoreLinkFileMetaValue,
      show_custom_date: showCustomDateMetaValue,
      custom_date: customDateMetaValue,
      featured_page_list: featuredPageListMetaValue,
      image_alt_text: imageAltTextMetaValue,
      learn_more_link: learnMoreLinkMetaValue,
      show_popup: showPopupMetaValue,
      event_date: eventDateMetaValue,
      event_start_date: eventStartDateMetaValue,
      event_end_date: eventEndDateMetaValue,
      featured: featuredMetaValue,
      place_holder_image_url: placeHolderImageUrlMetaValue,
      hide_from_list_view: hideFromListViewMetaValue,
    } = postMeta;

    const [defaultLinkText] = useSettings('custom.defaultLinkText') ?? '';
    const [learnMoreLabel, setLearnMoreLabel] = useState(
      learnMoreLabelMetaValue,
    );
    const [learnMoreType, setLearnMoreType] = useState(
      learnMoreTypeMetaValue || 'de_link',
    );

    const [showCustomDate, setShowCustomDate] = useState(
      showCustomDateMetaValue,
    );

    const [hideFromListView, setHideFromListView] = useState(
      hideFromListViewMetaValue,
    );

    const [customDate, setCustomDate] = useState(
      customDateMetaValue && showCustomDateMetaValue
        ? moment(customDateMetaValue).format()
        : moment().format(),
    );

    const [featured, setFeatured] = useState(featuredMetaValue);
    const featuredPageIds = featuredPageListMetaValue;
    const [featuredPageList, setFeaturedPageList] = useState([]);
    const [pagesListOptions, setPagesListOptions] = useState([]);
    const [imageAltText, setImageAltText] = useState(
      imageAltTextMetaValue || '',
    );
    const [placeholderImageUrl, setPlaceholderImageUrl] = useState(
      placeHolderImageUrlMetaValue,
    );

    const [eventDate, setEventDate] = useState(eventDateMetaValue);
    const [eventStartDate, setEventStartDate] = useState(
      eventDateMetaValue && eventStartDateMetaValue
        ? moment(eventStartDateMetaValue).format()
        : moment().format(),
    );

    const [eventEndDate, setEventEndDate] = useState(
      eventDateMetaValue && eventEndDateMetaValue
        ? moment(eventEndDateMetaValue).format()
        : moment().format(),
    );

    const [isPageListLoading, setPageListLoading] = useState(true);

    const setPagesData = (postList) => {
      const postsOptions = [];
      const featuredPages = [];
      postList.forEach((post) => {
        const {
          id,
          title: { rendered },
        } = post;

        if (currentPostId !== id && rendered) {
          const postData = { value: id, label: decodeUnicode(rendered) };
          postsOptions.push(postData);
          if (featuredPageIds.includes(parseInt(id))) {
            featuredPages.push(postData);
          }
        }
      });

      setFeaturedPageList(featuredPages);
      setPagesListOptions(postsOptions);
      setPageListLoading(false);
    };

    useEffect(() => {
      setPageListLoading(true);
      const query = {
        per_page: -1,
        order: 'asc',
        status: 'publish',
      };
      getPosts('page', setPagesData, query);
    }, []); // eslint-disable-line

    useEffect(() => {
      const text = learnMoreLabelMetaValue || defaultLinkText;
      setLearnMoreLabel(text);
      onMetaUpdate('learn_more_label', text);
    }, [defaultLinkText]); // eslint-disable-line

    const onMetaUpdate = (field, value) => {
      // update metadata.
      dispatch('core/editor').editPost({
        meta: {
          [field]: value,
        },
      });
    };

    return (
      <PluginDocumentSettingPanel
        className="common-post-options"
        name="common-post-options"
        title={__('Post Options', '')}
        initialOpen={true}
      >
        <div className="components-base-control">
          <label
            htmlFor="show-custom-date"
            className={'components-base-control__label'}
          >
            Show custom date
          </label>
          <ToggleControl
            id="show-custom-date"
            label={__('Show custom date')}
            checked={showCustomDate}
            onChange={(value) => {
              setShowCustomDate(value);
              onMetaUpdate('show_custom_date', value);
              if (value) {
                onMetaUpdate('custom_date', customDate);
              } else {
                onMetaUpdate('custom_date', '');
              }
            }}
          />
        </div>
        {showCustomDate && (
          <div className="components-base-control">
            <label
              htmlFor="custom-date"
              className={'components-base-control__label'}
            >
              Custom Date
            </label>
            <DateTimePicker
              id="custom-date"
              label={__('Custom date', '')}
              type="text"
              currentDate={customDate}
              onChange={(value) => {
                setCustomDate(value);
                onMetaUpdate('custom_date', value);
              }}
            />
          </div>
        )}
        <div className="components-base-control">
          <label
            htmlFor="featured-post"
            className={'components-base-control__label'}
          >
            Featured Post
          </label>
          <ToggleControl
            id="featured-post"
            label={__('Featured Post')}
            checked={featured}
            onChange={(value) => {
              setFeatured(value);
              onMetaUpdate('featured', value);
            }}
          />
        </div>

        {featured && (
          <div className="components-base-control featured-in-page">
            <label
              htmlFor="featured-in-page"
              className={'components-base-control__label'}
            >
              Feature In (Pages)
            </label>
            <Select
              id="featured-in-page"
              isMulti
              hideSelectedOptions={false}
              closeMenuOnSelect={false}
              placeholder={__('Featured In (Pages)', '')}
              options={pagesListOptions}
              value={featuredPageList}
              isLoading={isPageListLoading}
              onChange={(selectedPages) => {
                let selectedPageList = [];
                if (selectedPages) {
                  selectedPageList = selectedPages.map((selectedPage) => {
                    return parseInt(selectedPage.value);
                  });
                }
                setFeaturedPageList(selectedPages);
                onMetaUpdate('featured_page_list', selectedPageList);
              }}
            />
          </div>
        )}
        <div className="components-base-control">
          <label
            htmlFor="hide-from-view"
            className={'components-base-control__label'}
          >
            {__('Hide from List Views', '')}
          </label>
          <ToggleControl
            id="hide-from-view"
            label={__('Show | Hide', '')}
            help={__(
              'Enable this option to hide the post from appearing in list views.',
              'b3rg',
            )}
            checked={hideFromListView}
            onChange={(value) => {
              setHideFromListView(value);
              onMetaUpdate('hide_from_list_view', value);
            }}
          />
        </div>
        <TextControl
          label={__('Image alternate text', '')}
          type="text"
          value={imageAltText}
          onChange={(value) => {
            setImageAltText(value);
            onMetaUpdate('image_alt_text', value);
          }}
        />
        <TextControl
          label={__('Read more label', '')}
          type="text"
          placeholder={
            defaultLinkText && __(`Defaults to '${defaultLinkText}'`, '') // eslint-disable-line
          }
          value={learnMoreLabel}
          onChange={(value) => {
            setLearnMoreLabel(value);
            onMetaUpdate('learn_more_label', value);
          }}
        />
        <SelectControl
          label={__('Read more type', '')}
          options={[
            { value: B3RG_DE_LINK, label: 'Detail page Link' },
            { value: B3RG_IN_EX_LINK, label: 'Internal Link/External Link' },
            { value: B3RG_FI_LINK, label: 'File Link' },
            {
              value: B3RG_PO_LINK,
              label: 'Popup Link [content will appear in a popup]',
            },
            { value: B3RG_NONE, label: 'None' },
          ]}
          value={learnMoreType}
          onChange={(value) => {
            setLearnMoreType(value);
            onMetaUpdate('learn_more_type', value);
          }}
        />
        {learnMoreType === B3RG_IN_EX_LINK && (
          <InternalExternalLink
            {...{ learnMoreLinkMetaValue, onMetaUpdate, showPopupMetaValue }}
          />
        )}

        {learnMoreType === B3RG_FI_LINK && (
          <FileLink
            {...{
              onMetaUpdate,
              featuredImageUrl,
              learnMoreLinkFileMetaValue,
            }}
          />
        )}

        <div className="components-base-control">
          <label
            htmlFor="event-date"
            className={'components-base-control__label'}
          >
            Event date
          </label>
          <ToggleControl
            id="event-date"
            label={__('Event date')}
            checked={eventDate}
            onChange={(value) => {
              setEventDate(value);
              onMetaUpdate('event_date', value);
              if (value) {
                onMetaUpdate('event_start_date', eventStartDate);
                onMetaUpdate('event_end_date', eventEndDate);
              } else {
                onMetaUpdate('event_start_date', '');
                onMetaUpdate('event_end_date', '');
              }
            }}
          />
        </div>
        {eventDate && (
          <>
            <div className="components-base-control">
              <label
                htmlFor="event-start-date"
                className={'components-base-control__label'}
              >
                Event Start Date
              </label>
              <DateTimePicker
                id="event-start-date"
                label={__('Event Start Date', '')}
                type="text"
                currentDate={eventStartDate}
                onChange={(value) => {
                  setEventStartDate(value);
                  onMetaUpdate('event_start_date', value);
                  //Resetting the end date when selecting a higher start date
                  if (
                    moment(value).format() > moment(eventStartDate).format()
                  ) {
                    setEventEndDate(value);
                    onMetaUpdate('event_end_date', value);
                  }
                }}
              />
            </div>
            <div className="components-base-control">
              <label
                htmlFor="event-end-date"
                className={'components-base-control__label'}
              >
                Event End Date
              </label>
              <DateTimePicker
                id="event-end-date"
                label={__('Event End Date', '')}
                type="text"
                currentDate={eventEndDate}
                isInvalidDate={(value) => {
                  /* Disabling invalid days individually from the Calendar.Not considering the time when comparing dates because time is always 00:00:00 in the 'value'.
                   */
                  return (
                    moment(value).format('YYYY-MM-DD') <
                    moment(eventStartDate).format('YYYY-MM-DD')
                  );
                }}
                onChange={(value) => {
                  if (
                    moment(value).format() >= moment(eventStartDate).format()
                  ) {
                    setEventEndDate(value);
                    onMetaUpdate('event_end_date', value);
                  }
                }}
              />
            </div>
          </>
        )}

        <div className="components-base-control">
          <label
            htmlFor="placeholder-image"
            className="placeholder-image-label"
          >
            {__('Placeholder Image', 'b3rg')}
          </label>
          <MediaUploadCheck>
            <MediaUpload
              id="placeholder-image"
              value={placeholderImageUrl}
              allowedTypes={['image']}
              onSelect={(value) => {
                setPlaceholderImageUrl(value.url);
                onMetaUpdate('place_holder_image_url', value.url);
              }}
              render={({ open }) => (
                <>
                  {placeholderImageUrl && (
                    <div className="media-preview__placeholder-img">
                      <img src={placeholderImageUrl} alt="" />
                    </div>
                  )}

                  {placeholderImageUrl === '' && (
                    <Button
                      className={'editor-post-featured-image__toggle'}
                      onClick={open}
                    >
                      Choose an image
                    </Button>
                  )}

                  {placeholderImageUrl && (
                    <>
                      <Button onClick={open} isPrimary>
                        {__('Replace image', '')}
                      </Button>
                      <Button
                        onClick={() => {
                          setPlaceholderImageUrl('');
                          onMetaUpdate('place_holder_image_url', '');
                        }}
                        isSecondary
                        isDestructive
                      >
                        {__('Remove image', '')}
                      </Button>
                    </>
                  )}
                </>
              )}
            />
          </MediaUploadCheck>
        </div>
      </PluginDocumentSettingPanel>
    );
  };

  const PostOptions = (props) => {
    const { postType } = props;
    if (!postType || 'page' === postType || 'notification' === postType) {
      return null;
    }
    return <PostOptionsSettings {...props} />;
  };

  return withSelect((select) => {
    const postMeta = select('core/editor').getEditedPostAttribute('meta') || {};
    return {
      postMeta,
      currentPostId: select('core/editor').getCurrentPostId(),
      postType: select('core/editor').getCurrentPostType(),
      featuredImageUrl: select('core').getMedia(postMeta.learn_more_link_file)
        ?.title?.rendered,
    };
  })(PostOptions);
}

wp.domReady(() => {
  if (wp.editPost) {
    wp.plugins.registerPlugin('common-post-options', {
      render: registerPostOptionsPlugin(wp),
      icon: 'editor-table',
    });
  }
});
